import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getPublicOrder, payPublicOrder, requestPublicPaymentMethodChange } from "../api/client";
import { PACKAGE_TYPE_LABELS } from "../utils/packageLabels";
import FastSendLogo from "../components/FastSendLogo";
import LoadingIndicator from "../components/LoadingIndicator";
import ContactFooter from "../components/ContactFooter";

const STATUS_BADGE_CLASS = {
  scheduled: "warning",
  requested: "warning",
  accepted: "info",
  arrived_at_pickup: "info",
  picked_up: "info",
  in_transit: "info",
  delivered: "",
  completed: "",
  cancelled: "danger",
  failed: "danger"
};

// No account, no nav, no BottomNav/AdminNav — this is the one page in the
// app anyone can open with just a link, so it deliberately stands alone.
export default function PublicOrderView() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payGateway, setPayGateway] = useState("paystack");
  const [payEmail, setPayEmail] = useState("");
  const [payBusy, setPayBusy] = useState(false);
  const [payError, setPayError] = useState("");
  const [switchBusy, setSwitchBusy] = useState(false);
  const [switchError, setSwitchError] = useState("");
  const [switchMessage, setSwitchMessage] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await getPublicOrder(token);
      setOrder(data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "This link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
    // Light polling instead of a socket connection — this page has no
    // account to authenticate a socket room join with, and a receiver
    // checking status every so often doesn't need sub-second live updates.
    const interval = setInterval(load, 20000);
    return () => clearInterval(interval);
  }, [load]);

  const handleSwitchPaymentMethod = async (method) => {
    setSwitchError("");
    setSwitchMessage("");
    setSwitchBusy(true);
    try {
      const { data } = await requestPublicPaymentMethodChange(token, method);
      setOrder(data.order);
      setSwitchMessage(data.pendingConfirmation
        ? "⏳ Requested — waiting for the dispatcher to confirm switching to Pay on Delivery."
        : `✓ Switched to ${method === "cash" ? "Pay on Delivery (Cash)" : "Receiver Pays via link"}.`);
    } catch (err) {
      setSwitchError(err.response?.data?.error || "Could not change the payment method.");
    } finally {
      setSwitchBusy(false);
    }
  };

  const handlePay = async () => {
    setPayError("");
    setPayBusy(true);
    try {
      const inputMail = payEmail || "payment@fastsent.ng"
      const { data } = await payPublicOrder(token, payGateway, inputMail || undefined);
      if (data.paymentInit?.checkoutUrl) {
        window.location.href = data.paymentInit.checkoutUrl;
      }
    } catch (err) {
      setPayError(err.response?.data?.error || "Could not start payment. Please try again.");
    } finally {
      setPayBusy(false);
    }
  };

  return (
    <div className="user-app">
      <div className="user-app-main" style={{ maxWidth: "560px", margin: "0 auto" }}>
        <div className="page">
          <div style={{ textAlign: "center", marginBottom: "16px" }}>
            <FastSendLogo variant="full" size="medium" />
          </div>

          {loading ? (
            <LoadingIndicator label="Loading order details..." />
          ) : error ? (
            <div className="delivery-card" style={{ textAlign: "center" }}>
              <span style={{ fontSize: "40px" }}>🔗</span>
              <p className="error" style={{ marginTop: "8px" }}>{error}</p>
            </div>
          ) : (
            <>
              <div className="delivery-card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                  <h3 style={{ margin: 0 }}>Delivery #{order._id.slice(-6).toUpperCase()}</h3>
                  <span className={`status-badge ${STATUS_BADGE_CLASS[order.status] || ""}`}>
                    {order.status.replace(/_/g, " ").toUpperCase()}
                  </span>
                </div>
                {order.senderName && (
                  <p className="hint" style={{ marginBottom: "8px" }}>
                    Sent by <strong>{order.senderName}</strong>
                    {order.senderPhone && <> · <a href={`tel:${order.senderPhone}`}>{order.senderPhone}</a></>}
                  </p>
                )}

                <div className="info-row">
                  <span className="label">📍 Pickup</span>
                  <span className="value">{order.pickup?.address}</span>
                </div>
                {order.stops?.map((s, i) => (
                  <div className="info-row" key={i}>
                    <span className="label">Stop {i + 1}</span>
                    <span className="value">{s.address}</span>
                  </div>
                ))}
                <div className="info-row">
                  <span className="label">🏁 Drop-off</span>
                  <span className="value">{order.dropoff?.address}</span>
                </div>
                {order.dropoffContact?.name && (
                  <div className="info-row">
                    <span className="label">Receiver</span>
                    <span className="value">{order.dropoffContact.name}{order.dropoffContact.phone ? ` · ${order.dropoffContact.phone}` : ""}</span>
                  </div>
                )}
                <div className="info-row">
                  <span className="label">📦 Package</span>
                  <span className="value">
                    {PACKAGE_TYPE_LABELS[order.packageType] || order.packageType}
                    {order.packageDescription ? ` — ${order.packageDescription}` : ""}
                  </span>
                </div>
                {order.rider && (
                  <>
                    <div className="info-row">
                      <span className="label">🛵 Dispatcher</span>
                      <span className="value">{order.rider.name} · ⭐ {order.rider.rating?.toFixed(1) || "New"}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Phone</span>
                      <span className="value"><a href={`tel:${order.rider.phone}`}>{order.rider.phone}</a></span>
                    </div>
                  </>
                )}
                <div className="info-row">
                  <span className="label">Delivery Fee</span>
                  <span className="value">₦{order.priceSnapshot?.total?.toLocaleString()}</span>
                </div>
              </div>

              {order.deliveryToken?.code && !["cancelled", "failed"].includes(order.status) && (
                <div className="quote-box" style={{ marginTop: "16px" }}>
                  <h3>Delivery Verification</h3>
                  <p className="hint" style={{ marginBottom: "8px" }}>
                    Give this code to the dispatcher when the package arrives to confirm you received it.
                  </p>
                  <div style={{ fontSize: "26px", fontWeight: 800, letterSpacing: "4px", textAlign: "center", padding: "8px 0" }}>
                    {order.deliveryToken.code}
                  </div>
                </div>
              )}

              {!["completed", "cancelled", "failed"].includes(order.status) && (order.paymentMethod === "cash" || order.paymentMethod === "receiver_pay") && (
                <div className="quote-box" style={{ marginTop: "16px" }}>
                  <h3>💳 Delivery Payment</h3>

                  {order.paymentMethod === "receiver_pay" ? (
                    order.paymentStatus === "paid" ? (
                      <p className="hint" style={{ color: "var(--success)" }}>✅ Paid — thank you!</p>
                    ) : (
                      <>
                        <p className="hint" style={{ marginBottom: "8px" }}>
                          The sender asked you to cover the ₦{order.priceSnapshot?.total?.toLocaleString()} delivery fee.
                        </p>
                        <div className="form-field">
                          <label>Email (optional — for your receipt)</label>
                          <input type="email" value={payEmail} onChange={e => setPayEmail(e.target.value)} placeholder="you@example.com" />
                        </div>
                        <div className="form-field">
                          <label>Payment Method</label>
                          <select value={payGateway} onChange={e => setPayGateway(e.target.value)}>
                            <option value="paystack">Card / Bank Transfer (Paystack)</option>
                            <option value="flutterwave">Card / Bank Transfer (Flutterwave)</option>
                          </select>
                        </div>
                        {payError && <p className="error">{payError}</p>}
                        <button type="button" onClick={handlePay} disabled={payBusy} style={{ marginBottom: "12px" }}>
                          {payBusy ? "Redirecting..." : `Pay ₦${order.priceSnapshot?.total?.toLocaleString()}`}
                        </button>
                      </>
                    )
                  ) : order.paymentMethod === "bank_transfer" ? (
                    <p className="hint" style={{ marginBottom: "12px" }}>
                      🏦 This delivery is Pay on Delivery (Bank Transfer) — transfer ₦{order.priceSnapshot?.total?.toLocaleString()} to the company account when package arrives.
                    </p>
                  ) : (
                    <p className="hint" style={{ marginBottom: "12px" }}>
                      💵 This delivery is Cash on Delivery — pay the dispatcher ₦{order.priceSnapshot?.total?.toLocaleString()} in person when the package arrives.
                    </p>
                  )}

                  {/* Payment method switch */}
                  {order.paymentMethodChangeRequest?.status === "pending" ? (
                    <p className="hint" style={{ color: "var(--warning-hover)" }}>
                      ⏳ Waiting for the dispatcher to confirm switching to Pay on Delivery (Cash).
                    </p>
                  ) : (
                    <>
                      {order.paymentMethodChangeRequest?.status === "denied" && (
                        <p className="hint" style={{ color: "var(--danger-hover)", marginBottom: "8px" }}>
                          ❌ The dispatcher declined switching to Cash on Delivery — please pay with original method.
                        </p>
                      )}
                      {switchError && <p className="error">{switchError}</p>}
                      {switchMessage && <p className="hint" style={{ color: "var(--success)" }}>{switchMessage}</p>}
                      {order.paymentMethod === "receiver_pay" && order.paymentStatus !== "paid" && (
                        <button
                          type="button"
                          className="secondary"
                          disabled={switchBusy}
                          onClick={() => handleSwitchPaymentMethod("cash")}
                        >
                          {switchBusy ? "Requesting..." : "Switch to Pay on Delivery (Cash) instead"}
                        </button>
                      )}
                      {order.paymentMethod === "bank_transfer" && (
                        <button
                          type="button"
                          className="secondary"
                          disabled={switchBusy}
                          onClick={() => handleSwitchPaymentMethod("cash")}
                        >
                          {switchBusy ? "Requesting..." : "Switch to Pay on Delivery (Cash) instead"}
                        </button>
                      )}
                      {order.paymentMethod === "cash" && (
                        <div className="row" style={{ gap: "8px" }}>
                          <button
                            type="button"
                            className="secondary"
                            disabled={switchBusy}
                            onClick={() => handleSwitchPaymentMethod("bank_transfer")}
                          >
                            {switchBusy ? "Switching..." : "Switch to Bank Transfer"}
                          </button>
                          <button
                            type="button"
                            className="secondary"
                            disabled={switchBusy}
                            onClick={() => handleSwitchPaymentMethod("receiver_pay")}
                          >
                            {switchBusy ? "Switching..." : "Pay online via link"}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}

          <ContactFooter />
        </div>
      </div>
    </div>
  );
}
